import { Component, type ReactNode } from "react";
import { Text } from "@react-three/drei";

interface Props {
  children: ReactNode;
  label: string;
}

interface State {
  error: Error | null;
}

// Every panel-system consumer wraps in this. A local <Suspense> only
// catches thrown PROMISES, not thrown ERRORS — without an error boundary, a
// render bug anywhere in a panel (ShojiBacking, PanelText, …) propagates
// past Suspense with nothing to catch it, unmounting up the tree with no
// visible signal — exactly "the app just freezes, nothing shows" (the
// project's #1 lesson: never strand the player in VR with no feedback).
// Deliberately a raw <Text> with the troika default font, not
// PanelText/ShojiBacking, so the error report can't be broken by whatever
// broke the panel it's reporting on.
export class PanelErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack: string }) {
    console.error(`[PanelErrorBoundary:${this.props.label}]`, error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      return (
        <Text
          fontSize={0.05}
          color="#ff3333"
          anchorX="center"
          anchorY="middle"
          maxWidth={1.8}
          textAlign="center"
        >
          {`[${this.props.label}] ${this.state.error.message}`}
        </Text>
      );
    }
    return this.props.children;
  }
}
